import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { authReady, useAuth } from './composables/useAuth'
import Landing   from './views/Landing.vue'
import AppView   from './views/AppView.vue'
import AdminView from './views/AdminView.vue'
import NotFound  from './views/NotFound.vue'

declare module 'vue-router' {
  interface RouteMeta {
    /** Requires an authenticated user with the `active` claim. */
    requiresActive?: boolean
    /** Requires the `role: 'admin'` claim. */
    requiresAdmin?: boolean
    /** Reachable while signed out. */
    isPublic?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/',      name: 'landing',  component: Landing,   meta: { isPublic: true } },
  { path: '/app',   name: 'app',      component: AppView,   meta: { requiresActive: true } },
  { path: '/admin', name: 'admin',    component: AdminView, meta: { requiresActive: true, requiresAdmin: true } },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFound, meta: { isPublic: true } },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  // Without this await, a hard refresh on /app decides before Firebase has read
  // the persisted session from IndexedDB and bounces the user to the landing
  // page. Resolves immediately once the first token event has fired.
  await authReady

  const { isAuthed, isActive, isAdmin } = useAuth()

  // Already signed in and landing on the public route — go straight to the app.
  if (to.meta.isPublic && to.name === 'landing' && isActive.value) {
    return { name: 'app' }
  }

  if (to.meta.requiresActive) {
    if (!isAuthed.value) {
      // Preserve intent so the login form can return them where they were going.
      return { name: 'landing', query: { redirect: to.fullPath } }
    }
    if (!isActive.value) {
      return { name: 'landing', query: { error: 'account-inactive' } }
    }
  }

  if (to.meta.requiresAdmin && !isAdmin.value) {
    // Bounce to /app rather than /: they are legitimately signed in, just not an
    // admin. Sending them to the landing page would look like a sign-out.
    return { name: 'app' }
  }

  return true
})

export default router
